import { emptySplitApi as api } from "../../emptyApi";

// Types for the messaging monitoring API
export interface EmailLog {
  id: number;
  email: string;
  sent_date: string | null;
  response: string | null;
  template: string | null;
  subject: string | null;
  template_id: string | null;
  template_name: string | null;
  postmark_message_id: string | null;
  postmark_submitted_at: string | null;
  postmark_delivered_at: string | null;
  postmark_bounced_at: string | null;
  postmark_complained_at: string | null;
  status: 'pending' | 'sent' | 'delivered' | 'bounced' | 'complained' | 'failed';
  error_message: string | null;
  retry_count: number;
  context_data: any;
  created_at: string;
  updated_at: string;
  content_type_name: string | null;
  object_id: number | null;
  is_successful: boolean;
  is_failed: boolean;
}

export interface BounceRateStats {
  period: {
    days: number;
    start_date: string;
    end_date: string;
  };
  overall_stats: {
    total_emails: number;
    bounced_emails: number;
    complained_emails: number;
    delivered_emails: number;
    sent_emails: number;
    failed_emails: number;
    pending_emails: number;
    bounce_rate: number;
    complaint_rate: number;
    delivery_rate: number;
  };
  template_stats: Array<{
    template: string | null;
    total: number;
    sent: number;
    bounced: number;
    complained: number;
    delivered: number;
    failed: number;
    bounce_rate: number;
    complaint_rate: number;
    delivery_rate: number;
  }>;
  recent_bounces: EmailLog[];
}

export interface TestEmailRequest {
  email: string;
  subject?: string;
  message?: string;
}

export interface TestEmailResponse {
  success: boolean;
  message: string;
  email_log_id: number;
  postmark_message_id: string | null;
}

export interface EmailLogsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: EmailLog[];
}

// API Arguments
export interface GetBounceRateStatsApiArg {
  days?: number;
}

export interface GetEmailLogsApiArg {
  status?: string;
  template?: string;
  email?: string;
  days?: number;
  page?: number;
  page_size?: number;
}

export interface SendTestEmailApiArg {
  email: string;
  subject?: string;
  message?: string;
}

export interface GetEmailDetailsApiArg {
  id: number;
}

// API Responses
export type GetBounceRateStatsApiResponse = BounceRateStats;
export type GetEmailLogsApiResponse = EmailLogsResponse;
export type SendTestEmailApiResponse = TestEmailResponse;
export type GetEmailDetailsApiResponse = EmailLog;

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBounceRateStats: build.query<
      GetBounceRateStatsApiResponse,
      GetBounceRateStatsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/admin/v1/messaging-monitoring/bounce_rate_stats/`,
        params: {
          days: queryArg.days,
        },
      }),
    }),
    getEmailLogs: build.query<
      GetEmailLogsApiResponse,
      GetEmailLogsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/admin/v1/messaging-monitoring/email_logs/`,
        params: {
          status: queryArg.status,
          template: queryArg.template,
          email: queryArg.email,
          days: queryArg.days,
          page: queryArg.page,
          page_size: queryArg.page_size,
        },
      }),
    }),
    sendTestEmail: build.mutation<
      SendTestEmailApiResponse,
      SendTestEmailApiArg
    >({
      query: (queryArg) => ({
        url: `/api/admin/v1/messaging-monitoring/send_test_email/`,
        method: "POST",
        body: queryArg,
      }),
    }),
    getEmailDetails: build.query<
      GetEmailDetailsApiResponse,
      GetEmailDetailsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/admin/v1/messaging-monitoring/${queryArg.id}/email_details/`,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBounceRateStatsQuery,
  useGetEmailLogsQuery,
  useSendTestEmailMutation,
  useGetEmailDetailsQuery,
} = injectedRtkApi;
